import {DotFillIcon, McpIcon, NoEntryFillIcon, TerminalIcon, XCircleFillIcon} from '@primer/octicons-react'
import type {ReactNode} from 'react'
import {ResolvedPromptDisplay} from './ResolvedPromptDisplay'
import styles from './ResolvedPermission.module.css'
import {getPermissionLabel} from '../../utils/permission-prompt-helpers'

/**
 * Visual state of a permission prompt in the session-logs timeline.
 * - `pending` — request is open, awaiting a click (yellow dot)
 * - `expired` — resume window elapsed before the user clicked (red cancel)
 * - `denied` — user (or rule) rejected the tool call (red no-entry)
 */
export type PermissionEntryState = 'pending' | 'expired' | 'denied'

export interface ResolvedPermissionProps {
  /** The kind of permission: 'commands', 'shell', 'write', 'mcp', or a tool name */
  kind: string
  /** The tool requesting permission */
  toolName?: string
  /** Name of the MCP server providing the tool (for kind: "mcp") */
  serverName?: string
  /** What the tool intended to do — shown as the header title */
  description?: string
  /** Command text or filename */
  detail?: string
  /** Visual state of the prompt — drives the footer's icon, color, and text. */
  state: PermissionEntryState
  /** The denial result kind from the runtime (only used when state === 'denied'). */
  denialKind?: string
  /** Rich preview content (e.g. a diff preview). When provided, replaces detail. */
  previewContent?: ReactNode
}

/** Map denial kind to a human-friendly footer message */
function getDenialMessage(denialKind?: string): string {
  switch (denialKind) {
    case 'denied-by-rules':
      return 'Blocked by repository policy'
    case 'denied-by-content-exclusion-policy':
      return 'Blocked by content exclusion policy'
    case 'denied-no-approval-rule-and-could-not-request-from-user':
      return 'No approval rule configured'
    case 'aborted-by-user':
      return 'Aborted by user'
    case 'denied-interactively-by-user':
    default:
      return 'The user rejected this tool call'
  }
}

type FooterAppearance = {
  icon: React.ReactNode
  text: string
  textClassName?: string
  role?: 'status'
  ariaLive?: 'polite'
}

function getFooterAppearance(state: PermissionEntryState, denialKind: string | undefined): FooterAppearance {
  switch (state) {
    case 'pending':
      return {
        icon: <DotFillIcon size={16} className={styles.pendingIcon} />,
        text: 'Waiting for approval',
        textClassName: styles.pendingText,
        role: 'status',
        ariaLive: 'polite',
      }
    case 'expired':
      return {
        icon: <XCircleFillIcon size={16} className={styles.expiredIcon} />,
        text: 'Request timed out',
        textClassName: styles.expiredText,
      }
    case 'denied':
      return {
        icon: <NoEntryFillIcon size={16} className={styles.deniedIcon} />,
        text: getDenialMessage(denialKind),
        textClassName: styles.deniedText,
      }
  }
}

export function PermissionStatusFooter({state, denialKind}: {state: PermissionEntryState; denialKind?: string}) {
  const appearance = getFooterAppearance(state, denialKind)

  return (
    <div className={styles.footer} role={appearance.role} aria-live={appearance.ariaLive}>
      {appearance.icon}
      <span className={appearance.textClassName}>{appearance.text}</span>
    </div>
  )
}

/** Permission prompt entry shown in the session-logs timeline (pending / expired / denied). */
export function ResolvedPermission({
  kind,
  toolName,
  serverName,
  description,
  detail,
  state,
  denialKind,
  previewContent,
}: ResolvedPermissionProps) {
  const {isMcp, label: fallbackTitle} = getPermissionLabel(kind, toolName, serverName)
  const title = description || fallbackTitle

  return (
    <ResolvedPromptDisplay
      icon={isMcp ? <McpIcon size={16} /> : <TerminalIcon size={16} />}
      title={title}
      footer={<PermissionStatusFooter state={state} denialKind={denialKind} />}
    >
      {previewContent ?? detail}
    </ResolvedPromptDisplay>
  )
}
